<template>
<div class="flex-column checkout_product_row-border">
    <div class="products_row-item checkout_product_row-item">
        <div class="product_row-details" style="cursor:pointer;" target="_blank" @click="openProductPage(listItem.variation.item.seo_name)">
            <img :src="listItem.item.item_picture" :alt="listItem.item.item_name"
                class="products_row-card_image checkout_product_row-item_image" />
            <div class="product_row-description">
                <div class="title-xs">{{ listItem.item.item_name }}</div>
                <p class="product_row-id paragraph-details">#{{ listItem.item_no }}</p>
                <p class="product_row-size paragraph-details" ><span v-for="(variant, index) in (listItem?.item?.variants ?? [])" :key="index">
                    {{ variant }}&nbsp;</span>
                </p>
                <p class="product_row-quantity paragraph-details">Qty: {{ listItem.quantity }}</p>
            </div>
        </div>
        <div class="product_row-actions">
            <div class="products_row-quantity">
                <input type="number"
                    :ref="`list_item_${listItem.list_item_id}`"
                    class="products_row-quantity_field small" 
                    :value="listItem.quantity" 
                    @input="($event) => {
                        isUpdatingCartItem = true
                        listItem.original_quantity = listItem.original_quantity ?? listItem.quantity
                        listItem.quantity = $event.target.value
                    }"
                    :min="1"
                    :max="listItem.item.inventory_level"
                    @wheel.prevent="blurOnWheel"
                    @blur="handleQuantityInput(listItem, $event)"
                    :readonly="props.readonly"
                    :aria-label="`Quantity for ${listItem.item.item_name}`" 
                />
                <TrashIcon id="delete-button" style="cursor: pointer;" :alt="`Remove ${listItem.item.item_name}`" @click="openItemDelete" />
            </div>
            <div class="product_row-total_price title-s desktop-hidden-flex">
                <div class="paragraph-details products_row-price-free" v-if="props.freeItem">FREE</div>
                <div class="product_row-price-discount" v-if="listItem.origPrice && listItem.price != listItem.origPrice">${{ formatPrice(listItem.origPrice * listItem.quantity) }}</div>
                <div class="product_row-price-total" v-if="listItem.origPrice && listItem.price != listItem.origPrice">${{ formatPrice(listItem.price * listItem.quantity) }}</div>
                <div class="paragraph-details products_row-price" v-if="!listItem.origPrice || listItem.price == listItem.origPrice">${{ formatPrice(listItem.price * listItem.quantity) }}</div>
            </div>
        </div>
        <div class="product_row-total_price title-s mobile-hidden-flex">
            <div class="paragraph-details products_row-price-free" v-if="props.freeItem">FREE</div>
            <div class="product_row-price-discount" v-if="listItem.origPrice && listItem.price != listItem.origPrice">${{ formatPrice(listItem.origPrice * listItem.quantity) }}</div>
            <div class="product_row-price-total" v-if="listItem.origPrice && listItem.price != listItem.origPrice">${{ formatPrice(listItem.price * listItem.quantity) }}</div>
            <div class="paragraph-details products_row-price" v-if="!listItem.origPrice || listItem.price == listItem.origPrice">${{ formatPrice(listItem.price * listItem.quantity) }}</div>
        </div>
    </div>
    <p class="input-support-text invalid-state paragraph-details" v-if="props.isRestricted">This product is under a restriction. Please remove from cart to continue.</p>
    <div v-if="hasProp65(listItem)" class="flex-row-forced align-center" style="gap:0.5rem;">
        <WarningIconProp65 :id="`warning_${listItem.list_item_id}`" width="20" height="20" alt="Warning"/>
        <a @click.prevent="openProp65Modal()" class="input-support-text paragraph-details">
            <strong>WARNING:</strong> California Residents Click Here For California Prop 65 Warning.
        </a>
    </div>
    <p v-if="!readonly && +listItem.quantity > +listItem.item.inventory_level" class="input-support-text invalid-state paragraph-details">
        The quantity entered exceeds the maximum allowed of {{ listItem.item.inventory_level }}
    </p>    
</div>
</template>
<script lang="ts" setup>
    import { toRef, watch, type Ref, inject } from "vue";
    import { useLists } from "@/helpers/composables/useList";
    import { router } from "@inertiajs/vue3";
    import { formatPrice } from "@/helpers/composables/usePriceFormatting"
    import { useCheckoutD2CStore } from '@/store/checkoutD2CStore'; 
    import { useDialog } from "primevue/usedialog";
    import { storeToRefs } from "pinia";
    import Prop65WarningModal from "@/components/Prop65WarningModal.vue";
    import TrashIcon from "@icons/trash-icon.svg";
    import WarningIconProp65 from "@icons/warning-icon.svg";

    let route = inject('route');

    let dialog = useDialog();

    let list_item = defineModel("listItem", {
        type: Object,
        default: ()=>{
            return {};
        }
    });

    let props = defineProps({
        readonly: {
            type: Boolean,
            default: false
        },
        freeItem: {
            type: Boolean,
            default: false
        },
        isRestricted: {
            type: Boolean,
            default: false
        },
        isProp65: {
            type: Boolean,
            default: false
        }
    });

    const {
        recalculateTax, reloadOrderInfo, getShippingRates, reapplyCoupon
    } = useCheckoutD2CStore();

    const {
        isUpdatingCartItem
    } = storeToRefs(useCheckoutD2CStore());

    let { updateListItem } = useLists({ router, route });

    const emit = defineEmits(["openItemDeleteModal"]);

    const listItem = toRef({
        ...list_item.value,
        origPrice: list_item?.value?.unit_price,
        item_no: list_item?.value?.variation?.rothcoItem?.rothco_item_no,
        original_quantity: list_item?.value?.quantity,
        item: {
            item_name: list_item?.value?.variation.item.name,
            variants: list_item?.value?.variation.text_description,
            inventory_level: list_item?.value?.variation.inventory_level,
            item_picture: list_item?.value?.variation?.primary_image
                ? `https://www.rothco.com/upload/product/product/small/${list_item?.value?.variation.primary_image.image_filename}`
                : null
        }
    }) as Ref;

    watch(list_item, (newVal)=>{
        listItem.value = {
            ...list_item.value,
            origPrice: list_item?.value?.unit_price,
            item_no: list_item?.value?.variation?.rothcoItem?.rothco_item_no,
            original_quantity: list_item?.value?.quantity,
            item: {
                item_name: list_item?.value?.variation.item.name,
                variants: list_item?.value?.variation.text_description,
                inventory_level: list_item?.value?.variation.inventory_level,
                item_picture: list_item?.value?.variation?.primary_image
                    ? `https://www.rothco.com/upload/product/product/small/${list_item?.value?.variation.primary_image.image_filename}`
                    : null
            }
        }
    });

    let openItemDelete = function () {
        emit("openItemDeleteModal", listItem);
    };

    let handleQuantityInput = (row, event: Event)=>{
        if(+listItem.value.quantity <= 0){
            listItem.value.quantity = 1;
        }else if(+listItem.value.quantity > +listItem.value.item.inventory_level){
            listItem.value.quantity = listItem.value.item.inventory_level;
        }
        if(listItem.value.quantity != listItem.value.original_quantity){
            listItem.value.original_quantity = listItem.value.quantity;
            updateItemQty(listItem.value);
        }else{
            isUpdatingCartItem.value = false;
        }
    };

    const updateItemQty = function (listItem) {
        updateListItem(listItem, {
            afterFetch: () => {
                isUpdatingCartItem.value = false;
                recalculateTax();
                getShippingRates();
                reapplyCoupon();
                reloadOrderInfo();
            },
            onFetchError: ()=>{
                isUpdatingCartItem.value = false;
            }
        });
    };

    function blurOnWheel(event: WheelEvent) {
        event.target.blur();
    }

    function openProp65Modal(){
        dialog.open(Prop65WarningModal, {
            props: {
                showHeader: false,
                modal: true
            },
            data: {
                prop65Spec: listItem.value?.prop_65_specs.map(spec => spec.value.spec_value)
            }
        });
    }

    function hasProp65(listItem) {
        return listItem?.prop_65_specs && listItem?.prop_65_specs.length > 0;
    }

    const openProductPage = (seo_name)=>{
        window.open(route('productDetail.show', [seo_name]), "_blank");
    }
</script>